import React from 'react';
import { easeOut, motion } from 'framer-motion';
import './WorksPage.scss';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Lenis from 'lenis';


const WorksPage = ({ pages }) => {
    const containerRef = React.useRef(null);
    const contentInnerRef = React.useRef(null);
    const [isMobile, setIsMobile] = React.useState(false);
    React.useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const handleWorkClick = () => {
        if (containerRef.current) {
            sessionStorage.setItem('works-scroll-pos', containerRef.current.scrollTop);
        }
    };

    React.useEffect(() => {
        if (!containerRef.current || !contentInnerRef.current) return;

        const lenis = new Lenis({
            wrapper: containerRef.current,
            content: contentInnerRef.current,
            wheelEventsTarget: containerRef.current,
            touchEventsTarget: containerRef.current,
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.9,
        });

        let rafId;
        function raf(time) {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        }

        rafId = requestAnimationFrame(raf);

        return () => {
            lenis.destroy();
            cancelAnimationFrame(rafId);
        };
    }, [pages]);

    // Return early if no works are available
    if (!pages || pages.length === 0) return <div>No works found</div>;

    // Group works by year extracted from date
    const grouped = pages.reduce((acc, work) => {
        const year = new Date(work.date).getFullYear();
        acc[year] = acc[year] || [];
        acc[year].push(work);
        return acc;
    }, {});

    // Get sorted years in descending order
    const years = Object.keys(grouped).sort((a, b) => b - a);

    return (
        // Page container with fade animation
        <>
            <Helmet>
                <title>works - nthng</title>
                <meta name="description" content="Works by nthng" />
                <link rel="canonical" href="https://nthng.nl/works" />
                <meta property="og:title" content="Works - nthng" />
                <meta property="og:description" content="Works by nthng" />
                <meta property="og:url" content="https://nthng.nl/works" />
                <meta property="og:type" content="music.album" />
                <meta property="og:image" content="https://nthng.nl/content/nthng_unfinished.webp" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Works - nthng" />
                <meta name="twitter:description" content="Works by nthng" />
                <meta name="twitter:image" content="https://nthng.nl/content/nthng_unfinished.webp" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        "name": "Works & Releases - nthng",
                        "description": "Complete discography of nthng - Electronic music releases, albums, EPs, and remixes",
                        "url": "https://nthng.nl/works",
                        "mainEntity": {
                            "@type": "MusicGroup",
                            "name": "nthng"
                        }
                    })}
                </script>
            </Helmet>
            <motion.div
                className="works-container"
                ref={containerRef}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: easeOut }}
            >
                <div className="works-content">
                    <div className="works-content-inner" ref={contentInnerRef}>
                        {years.map(year => {
                            // Sort works for the current year by date (latest first)
                            const sortedWorks = grouped[year].sort((a, b) => new Date(b.date) - new Date(a.date));

                            return (
                                <React.Fragment key={year}>
                                    {sortedWorks.map((work, idx) => {
                                        const isLast = idx === sortedWorks.length - 1;

                                        return (
                                            <div
                                                className={`work-row${isLast ? ' last-in-group' : ''}`}
                                                key={work.id}
                                            >
                                                {/* Only show year for the first work in each year group */}
                                                <p className="work-year">{idx === 0 ? year : ''}</p>
                                                <div className="title-category-container">
                                                    <p className="work-title">
                                                        <Link to={`/${work.url}`} onClick={handleWorkClick}>
                                                            {work.title}
                                                        </Link>
                                                    </p>
                                                    <p className="work-category">{work.category}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </motion.div>
        </>
    );
}

export default WorksPage;


